import { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./account.css";
import Navbar from "./Navbar";

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://investor-backend-s4w5.onrender.com");

const FIXED_TENURE_MONTHS = 60;

export default function AccountDashboard() {
  const [users, setUsers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [interestForm, setInterestForm] = useState({});
  const [showArchived, setShowArchived] = useState(false);

  // ✅ new states for upload/edit docs from accountant side
  const [editingUserId, setEditingUserId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    fullName: "",
    aadharNumber: "",
    panNumber: "",
    accountNumber: "",
    ifsc: "",
    bankName: "",
  });
  const [uploadFiles, setUploadFiles] = useState({
    aadhar: null,
    pan: null,
    bankProof: null,
  });

  useEffect(() => {
    loadInvestments();
  }, []);

  useEffect(() => {
    if (investments.length > 0) {
      loadUsersFromInvestments();
    } else {
      setUsers([]);
    }
  }, [investments]);

  const loadUsersFromInvestments = async () => {
    try {
      const uniqueUserIds = [
        ...new Set(
          investments
            .map((inv) => inv.userId)
            .filter(Boolean)
            .map((id) =>
              typeof id === "object" && id !== null ? id._id || "" : String(id),
            )
            .filter(Boolean),
        ),
      ];

      const responses = await Promise.all(
        uniqueUserIds.map(async (userId) => {
          try {
            const res = await axios.get(
              `${API_BASE_URL}/api/users/documents/${userId}`,
            );

            return {
              _id: userId,
              investorId: res.data?.investorId || "",
              name:
                res.data?.name ||
                res.data?.fullName ||
                res.data?.customerName ||
                "Investor",
              fullName:
                res.data?.fullName ||
                res.data?.name ||
                res.data?.customerName ||
                "",
              aadharNumber: res.data?.aadharNumber || "",
              panNumber: res.data?.panNumber || "",
              accountNumber: res.data?.bankDetails?.accountNumber || "",
              ifsc: res.data?.bankDetails?.ifsc || "",
              bankName: res.data?.bankDetails?.bankName || "",
              documents: {
                aadhar: res.data?.documents?.aadhar || res.data?.aadhar || "",
                pan: res.data?.documents?.pan || res.data?.pan || "",
                bankProof:
                  res.data?.documents?.bankProof || res.data?.bankProof || "",
              },
              kycStatus: res.data?.kycStatus || "pending",
            };
          } catch (error) {
            console.log(`User documents fetch failed for ${userId}`, error);
            return null;
          }
        }),
      );

      setUsers(responses.filter(Boolean));
    } catch (err) {
      console.log("KYC users load error:", err);
      setUsers([]);
    }
  };

  const loadInvestments = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/investments`);
      setInvestments(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const verify = async (id) => {
    try {
      if (!id) {
        alert("User ID missing ❌");
        return;
      }

      const res = await axios.put(`${API_BASE_URL}/api/users/verify/${id}`);
      alert(res.data?.message || "KYC Verified ✅");
      loadInvestments();
      loadUsersFromInvestments();
    } catch (err) {
      console.log("KYC verify error:", err);
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          `Error verifying KYC ❌ (${err?.response?.status || "No status"})`,
      );
    }
  };

  const verifyDepositWithBank = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/investments/account-verify/${id}`);
      alert("Deposit verified with bank ✅");
      loadInvestments();
    } catch (err) {
      console.log(err);
      alert("Error verifying deposit ❌");
    }
  };

  const releaseCapital = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/investments/release-capital/${id}`);
      alert("Capital Released & Archived ✅");
      loadInvestments();
    } catch (err) {
      console.log(err);
      alert(err?.response?.data?.message || "Error releasing capital ❌");
    }
  };

  const openUploadedFile = (fileName) => {
    if (!fileName) return;
    window.open(`${API_BASE_URL}/uploads/${fileName}`, "_blank");
  };

  const handleInterestChange = (investmentId, field, value) => {
    setInterestForm((prev) => ({
      ...prev,
      [investmentId]: {
        ...prev[investmentId],
        [field]: value,
      },
    }));
  };

  // ✅ open upload form from accountant side
  const openUploadEditor = (user) => {
    setEditingUserId(user._id);
    setUploadForm({
      fullName: user.fullName || user.name || "",
      aadharNumber: user.aadharNumber || "",
      panNumber: user.panNumber || "",
      accountNumber: user.accountNumber || "",
      ifsc: user.ifsc || "",
      bankName: user.bankName || "",
    });
    setUploadFiles({
      aadhar: null,
      pan: null,
      bankProof: null,
    });
  };

  const closeUploadEditor = () => {
    setEditingUserId(null);
    setUploadForm({
      fullName: "",
      aadharNumber: "",
      panNumber: "",
      accountNumber: "",
      ifsc: "",
      bankName: "",
    });
    setUploadFiles({
      aadhar: null,
      pan: null,
      bankProof: null,
    });
  };

  const handleUploadFormChange = (field, value) => {
    setUploadForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (field, file) => {
    setUploadFiles((prev) => ({
      ...prev,
      [field]: file || null,
    }));
  };

  const handleAccountantUpload = async (userId) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("fullName", uploadForm.fullName || "");
      formData.append("aadharNumber", uploadForm.aadharNumber || "");
      formData.append("panNumber", uploadForm.panNumber || "");
      formData.append("accountNumber", uploadForm.accountNumber || "");
      formData.append("ifsc", uploadForm.ifsc || "");
      formData.append("bankName", uploadForm.bankName || "");

      if (uploadFiles.aadhar) formData.append("aadhar", uploadFiles.aadhar);
      if (uploadFiles.pan) formData.append("pan", uploadFiles.pan);
      if (uploadFiles.bankProof)
        formData.append("bankProof", uploadFiles.bankProof);

      const res = await axios.post(
        `${API_BASE_URL}/api/users/upload-docs/${userId}`,
        formData,
      );

      alert(res.data?.msg || "Documents uploaded successfully ✅");
      closeUploadEditor();
      await loadInvestments();
      await loadUsersFromInvestments();
    } catch (err) {
      console.log("Accountant upload error:", err);
      alert(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Upload failed ❌",
      );
    } finally {
      setUploading(false);
    }
  };

  const startOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return startOfDay(d);
  };

  const addMonths = (date, months) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
  };

  const diffDaysInclusive = (startDate, endDate) => {
    const oneDay = 1000 * 60 * 60 * 24;
    const start = startOfDay(startDate);
    const end = startOfDay(endDate);
    return Math.floor((end - start) / oneDay) + 1;
  };

  const formatScheduleDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-IN");
  };

  const getTotalQuartersForInvestment = (investment) => {
    const tenureMonths = Number(investment?.tenure || FIXED_TENURE_MONTHS);
    return Math.ceil(tenureMonths / 3);
  };

  const getFirstQuarterEnd = (depositDate) => {
    const d = new Date(depositDate);
    const year = d.getFullYear();
    const month = d.getMonth();

    if (month >= 0 && month <= 5) {
      return new Date(year, 5, 30);
    }

    if (month >= 6 && month <= 8) {
      return new Date(year, 8, 30);
    }

    return new Date(year, 11, 31);
  };

  const getNextQuarterEnd = (previousQuarterEnd) => {
    const d = new Date(previousQuarterEnd);
    const month = d.getMonth();
    const year = d.getFullYear();

    if (month === 5) return new Date(year, 8, 30);
    if (month === 8) return new Date(year, 11, 31);
    if (month === 11) return new Date(year + 1, 2, 31);
    return new Date(year, 5, 30);
  };

  const getMaturityDate = (investment) => {
    if (investment?.maturityDate) {
      return new Date(investment.maturityDate);
    }

    const baseDate = investment?.verifiedAt
      ? new Date(investment.verifiedAt)
      : investment?.depositDate
        ? new Date(investment.depositDate)
        : new Date();

    return addMonths(
      baseDate,
      Number(investment?.tenure || FIXED_TENURE_MONTHS),
    );
  };

  const buildQuarterSchedule = (investment) => {
    const baseStartDate = investment?.verifiedAt
      ? new Date(investment.verifiedAt)
      : investment?.depositDate
        ? new Date(investment.depositDate)
        : new Date();

    const depositDate = startOfDay(baseStartDate);
    const maturityDate = startOfDay(getMaturityDate(investment));

    const schedule = [];
    let currentStart = depositDate;
    let currentEnd = startOfDay(getFirstQuarterEnd(depositDate));
    let i = 1;

    while (currentStart <= maturityDate) {
      let finalEnd = new Date(currentEnd);

      if (finalEnd > maturityDate) {
        finalEnd = maturityDate;
      }

      const days =
        currentStart <= finalEnd
          ? diffDaysInclusive(currentStart, finalEnd)
          : 0;

      const isFirstQuarter = i === 1;

      const isFinalPartialQuarter =
        finalEnd.getTime() === maturityDate.getTime() &&
        (finalEnd.getDate() !== currentEnd.getDate() ||
          finalEnd.getMonth() !== currentEnd.getMonth() ||
          finalEnd.getFullYear() !== currentEnd.getFullYear());

      schedule.push({
        quarterNo: i,
        label: `Y${Math.ceil(i / 4)}-Q${((i - 1) % 4) + 1}`,
        start: new Date(currentStart),
        end: new Date(finalEnd),
        days,
        isDayWise: isFirstQuarter || isFinalPartialQuarter,
      });

      currentStart = addDays(finalEnd, 1);
      currentEnd = getNextQuarterEnd(currentEnd);
      i += 1;
    }

    return schedule;
  };

  const calculateQuarterInterest = (investment, quarterLabel) => {
    if (!investment || !quarterLabel) return 0;

    const schedule = buildQuarterSchedule(investment);
    const quarter = schedule.find((q) => q.label === quarterLabel);

    if (!quarter) return 0;

    const amount = Number(investment.depositAmount || 0);
    const rate = Number(investment.interestRate || 0);

    const annualInterest = (amount * rate) / 100;
    const perDayInterest = annualInterest / 365;
    const fullQuarterInterest = annualInterest / 4;

    if (quarter.isDayWise) {
      return Number((perDayInterest * quarter.days).toFixed(2));
    }

    return Number(fullQuarterInterest.toFixed(2));
  };

  const getQuarterOptions = (investment) => {
    const existingQuarters = new Set(
      (investment.quarterlyInterests || []).map((q) => q.quarterLabel),
    );

    return buildQuarterSchedule(investment).map((quarter) => ({
      value: quarter.label,
      label: `Year ${Math.ceil(quarter.quarterNo / 4)} - Quarter ${
        ((quarter.quarterNo - 1) % 4) + 1
      }`,
      disabled: existingQuarters.has(quarter.label),
    }));
  };

  const getQuarterDueDate = (quarterEnd) => {
    const d = new Date(quarterEnd);
    d.setDate(d.getDate() + 15);
    return d;
  };

  const buildDownloadSchedule = (investment) => {
    const schedule = buildQuarterSchedule(investment);

    return schedule.map((q, index) => {
      const existingQuarter = (investment.quarterlyInterests || []).find(
        (item) => item.quarterLabel === q.label,
      );

      return {
        sr: `Q${index + 1}`,
        period: `${formatScheduleDate(q.start)} to ${formatScheduleDate(q.end)}`,
        dueDate: formatScheduleDate(getQuarterDueDate(q.end)),
        interest: Number(
          calculateQuarterInterest(investment, q.label).toFixed(2),
        ),
        paidDate: existingQuarter?.paidDate
          ? formatScheduleDate(existingQuarter.paidDate)
          : "",
      };
    });
  };

  const downloadQuarterSchedulePDF = (investment) => {
    const rows = buildDownloadSchedule(investment);
    const totalInterest = rows.reduce(
      (sum, row) => sum + Number(row.interest || 0),
      0,
    );

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(24, 102, 53);
    doc.rect(12, 12, pageWidth - 24, 18, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("Quarterly Interest Schedule", pageWidth / 2, 23, {
      align: "center",
    });

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`Investor Name: ${investment.customerName || "-"}`, 14, 40);
    doc.text(
      `Deposit Amount: Rs. ${Number(investment.depositAmount || 0).toFixed(2)}`,
      14,
      47,
    );
    doc.text(`Interest Rate: ${investment.interestRate || 9}%`, 14, 54);
    doc.text(
      `Deposit Date: ${
        investment.depositDate
          ? new Date(investment.depositDate).toLocaleDateString("en-IN")
          : "-"
      }`,
      110,
      40,
    );
    doc.text(
      `Tenure: ${investment.tenure ? `${investment.tenure / 12} Years` : "5 Years"}`,
      110,
      47,
    );
    doc.text(
      `Maturity Date: ${
        investment.maturityDate
          ? new Date(investment.maturityDate).toLocaleDateString("en-IN")
          : formatScheduleDate(getMaturityDate(investment))
      }`,
      110,
      54,
    );

    autoTable(doc, {
      startY: 62,
      head: [
        [
          "Qtr.",
          "Period\n(From - To)",
          "Due Date",
          "Interest\n(Rs.)",
          "Paid Date",
        ],
      ],
      body: rows.map((row) => [
        row.sr,
        row.period,
        row.dueDate,
        `Rs. ${row.interest.toFixed(2)}`,
        row.paidDate || "-",
      ]),
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 4,
        lineColor: [32, 94, 60],
        lineWidth: 0.2,
        textColor: [20, 20, 20],
        valign: "middle",
      },
      headStyles: {
        fillColor: [24, 102, 53],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        minCellHeight: 14,
      },
      bodyStyles: {
        halign: "left",
        valign: "middle",
      },
      alternateRowStyles: {
        fillColor: [237, 245, 240],
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 20 },
        1: { cellWidth: 80, halign: "left" },
        2: { halign: "center", cellWidth: 32 },
        3: { halign: "center", cellWidth: 30 },
        4: { halign: "center", cellWidth: 28 },
      },
      margin: { left: 12, right: 12 },
    });

    const finalY = doc.lastAutoTable.finalY || 70;

    doc.setFillColor(24, 102, 53);
    doc.rect(12, finalY + 8, pageWidth - 24, 10, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text("TOTAL INTEREST", 16, finalY + 14.5);
    doc.text(`Rs. ${totalInterest.toFixed(2)}`, pageWidth - 16, finalY + 14.5, {
      align: "right",
    });

    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(
      "This schedule is generated automatically based on verified deposit date, financial quarter rules, and interest payout records.",
      14,
      finalY + 25,
    );

    const safeName = (investment.customerName || "Investor").replace(
      /\s+/g,
      "_",
    );
    doc.save(`${safeName}_Quarterly_Interest_Schedule.pdf`);
  };

  const addQuarterlyInterest = async (investment) => {
    const investmentId = investment._id;
    const row = interestForm[investmentId] || {};
    const quarterLabel = row.quarterLabel || "";
    const paidDate = row.paidDate || new Date().toISOString().split("T")[0];

    const amount = calculateQuarterInterest(investment, quarterLabel);

    if (!quarterLabel.trim()) {
      alert("Please select quarter ❗");
      return;
    }

    if (!amount || amount <= 0) {
      alert("Invalid quarter interest amount ❗");
      return;
    }

    const alreadyExists = (investment.quarterlyInterests || []).some(
      (q) => q.quarterLabel === quarterLabel,
    );

    if (alreadyExists) {
      alert("This quarter is already added ❗");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/investments/add-quarterly-interest/${investmentId}`,
        {
          quarterLabel,
          amount,
          paidDate,
        },
      );

      alert("Quarterly interest sent to admin for approval ✅");

      setInterestForm((prev) => ({
        ...prev,
        [investmentId]: {
          quarterLabel: "",
          paidDate: "",
        },
      }));

      loadInvestments();
    } catch (err) {
      console.log(err);
      alert(
        err.response?.data?.message || "Error sending quarterly interest ❌",
      );
    }
  };

  const sendInterestToCustomer = async (investmentId, quarterId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/investments/send-interest-to-customer/${investmentId}/${quarterId}`,
      );

      alert("Sent to client ✅ (Visible after 24 hours)");
      loadInvestments();
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Error sending to client ❌");
    }
  };

  const totalUsers = users.length;
  const verifiedUsers = users.filter((u) => u.kycStatus === "verified").length;
  const verifiedDeposits = investments.filter((i) => i.accountVerified).length;
  const pendingDepositVerifications = investments.filter(
    (i) => !i.accountVerified,
  ).length;

  const activeInvestments = investments.filter((inv) => !inv.isArchived);
  const archivedInvestments = investments.filter((inv) => inv.isArchived);

  return (
    <>
      <Navbar />
      <div className="account-dashboard">
        <div className="account-header">
          <h1>Finance Dashboard</h1>
          <p>
            KYC, bank verification, quarterly interest processing & archived
            investments
          </p>
        </div>

        <div className="account-stats">
          <div className="stat-card">
            <span className="stat-label">Total Users</span>
            <span className="stat-value">{totalUsers}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">KYC Verified</span>
            <span className="stat-value">{verifiedUsers}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">Bank Verified Deposits</span>
            <span className="stat-value">{verifiedDeposits}</span>
          </div>

          <div className="stat-card">
            <span className="stat-label">Pending Deposit Verify</span>
            <span className="stat-value">{pendingDepositVerifications}</span>
          </div>
        </div>

        <section className="panel">
          <div className="panel-header">
            <h2>KYC Verification</h2>
          </div>

          <div className="table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Lender ID</th>
                  <th>Name</th>
                  <th>Full Name</th>
                  <th>Aadhar No.</th>
                  <th>PAN No.</th>
                  <th>Account No.</th>
                  <th>IFSC</th>
                  <th>Bank Name</th>
                  <th>Aadhar File</th>
                  <th>PAN File</th>
                  <th>Bank Proof</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {users.length > 0 ? (
                  users.map((u) => (
                    <div key={u._id} style={{ display: "contents" }}>
                      <tr key={u._id}>
                        <td>{u.investorId || "-"}</td>
                        <td>{u.name || "-"}</td>
                        <td>{u.fullName || "-"}</td>
                        <td>{u.aadharNumber || "-"}</td>
                        <td>{u.panNumber || "-"}</td>
                        <td>{u.accountNumber || "-"}</td>
                        <td>{u.ifsc || "-"}</td>
                        <td>{u.bankName || "-"}</td>

                        <td>
                          {u.documents?.aadhar ? (
                            <button
                              className="link-btn"
                              onClick={() =>
                                openUploadedFile(u.documents.aadhar)
                              }
                            >
                              View File
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {u.documents?.pan ? (
                            <button
                              className="link-btn"
                              onClick={() => openUploadedFile(u.documents.pan)}
                            >
                              View File
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          {u.documents?.bankProof ? (
                            <button
                              className="link-btn"
                              onClick={() =>
                                openUploadedFile(u.documents.bankProof)
                              }
                            >
                              View File
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          <span
                            className={
                              u.kycStatus === "verified"
                                ? "badge badge-success"
                                : "badge badge-warning"
                            }
                          >
                            {u.kycStatus || "pending"}
                          </span>
                        </td>

                        <td
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <button
                            className="primary-btn"
                            style={{ background: "#2563eb" }}
                            onClick={() => openUploadEditor(u)}
                          >
                            {u.documents?.aadhar ||
                            u.documents?.pan ||
                            u.documents?.bankProof
                              ? "Edit Docs"
                              : "Upload Docs"}
                          </button>

                          {u.kycStatus === "pending" ? (
                            <button
                              className="primary-btn"
                              onClick={() => verify(u._id)}
                            >
                              Verify
                            </button>
                          ) : (
                            <span className="done-text">Verified</span>
                          )}
                        </td>
                      </tr>

                      {editingUserId === u._id && (
                        <tr>
                          <td
                            colSpan="13"
                            style={{ background: "#f8fafc", padding: "16px" }}
                          >
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(3, minmax(220px, 1fr))",
                                gap: "12px",
                                alignItems: "center",
                              }}
                            >
                              <input
                                type="text"
                                placeholder="Full Name"
                                value={uploadForm.fullName}
                                onChange={(e) =>
                                  handleUploadFormChange(
                                    "fullName",
                                    e.target.value,
                                  )
                                }
                                className="field"
                              />

                              <input
                                type="text"
                                placeholder="Aadhar Number"
                                value={uploadForm.aadharNumber}
                                onChange={(e) =>
                                  handleUploadFormChange(
                                    "aadharNumber",
                                    e.target.value,
                                  )
                                }
                                className="field"
                              />

                              <input
                                type="text"
                                placeholder="PAN Number"
                                value={uploadForm.panNumber}
                                onChange={(e) =>
                                  handleUploadFormChange(
                                    "panNumber",
                                    e.target.value,
                                  )
                                }
                                className="field"
                              />

                              <input
                                type="text"
                                placeholder="Account Number"
                                value={uploadForm.accountNumber}
                                onChange={(e) =>
                                  handleUploadFormChange(
                                    "accountNumber",
                                    e.target.value,
                                  )
                                }
                                className="field"
                              />

                              <input
                                type="text"
                                placeholder="IFSC"
                                value={uploadForm.ifsc}
                                onChange={(e) =>
                                  handleUploadFormChange("ifsc", e.target.value)
                                }
                                className="field"
                              />

                              <input
                                type="text"
                                placeholder="Bank Name"
                                value={uploadForm.bankName}
                                onChange={(e) =>
                                  handleUploadFormChange(
                                    "bankName",
                                    e.target.value,
                                  )
                                }
                                className="field"
                              />

                              <div>
                                <label
                                  style={{
                                    fontWeight: "600",
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Aadhar File
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) =>
                                    handleFileChange(
                                      "aadhar",
                                      e.target.files?.[0],
                                    )
                                  }
                                />
                              </div>

                              <div>
                                <label
                                  style={{
                                    fontWeight: "600",
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  PAN File
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) =>
                                    handleFileChange("pan", e.target.files?.[0])
                                  }
                                />
                              </div>

                              <div>
                                <label
                                  style={{
                                    fontWeight: "600",
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Bank Proof
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) =>
                                    handleFileChange(
                                      "bankProof",
                                      e.target.files?.[0],
                                    )
                                  }
                                />
                              </div>
                            </div>

                            <div
                              style={{
                                marginTop: "16px",
                                display: "flex",
                                gap: "10px",
                              }}
                            >
                              <button
                                className="primary-btn"
                                onClick={() => handleAccountantUpload(u._id)}
                                disabled={uploading}
                                style={{ background: "#16a34a" }}
                              >
                                {uploading ? "Uploading..." : "Save Documents"}
                              </button>

                              <button
                                className="primary-btn"
                                onClick={closeUploadEditor}
                                style={{ background: "#6b7280" }}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </div>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="empty-cell">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2>Loan Approval Process</h2>
          </div>

          <div className="table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Investor ID</th>
                  <th>Investor Name</th>
                  <th>Deposit Amount</th>
                  <th>Interest Rate</th>
                  <th>Tenure</th>
                  <th>Total Quarters</th>
                  <th>Deposit Date</th>
                  <th>Bank Verify Status</th>
                  <th>Bank Verify</th>
                  <th>Add Quarterly Interest</th>
                  <th>Interest Entries</th>
                  <th>Schedule</th>
                  <th>Release</th>
                </tr>
              </thead>

              <tbody>
                {activeInvestments.length > 0 ? (
                  activeInvestments.map((inv) => {
                    const selectedQuarter =
                      interestForm[inv._id]?.quarterLabel || "";

                    const autoInterest = selectedQuarter
                      ? calculateQuarterInterest(inv, selectedQuarter)
                      : 0;

                    const totalQuarters = getTotalQuartersForInvestment(inv);
                    const quarterOptions = getQuarterOptions(inv);

                    return (
                      <tr key={inv._id}>
                        <td>{inv.investorId || "-"}</td>
                        <td>{inv.customerName || "-"}</td>
                        <td>
                          ₹{" "}
                          {Number(inv.depositAmount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </td>
                        <td>{inv.interestRate || 9}%</td>
                        <td>
                          {inv.tenure ? `${inv.tenure / 12} Years` : "5 Years"}
                        </td>
                        <td>{totalQuarters}</td>
                        <td>
                          {inv.depositDate
                            ? new Date(inv.depositDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "-"}
                        </td>

                        <td>
                          <span
                            className={
                              inv.accountVerified
                                ? "badge badge-success"
                                : "badge badge-warning"
                            }
                          >
                            {inv.accountVerified ? "Verified" : "Pending"}
                          </span>
                        </td>

                        <td>
                          {!inv.accountVerified ? (
                            <button
                              className="primary-btn"
                              onClick={() => verifyDepositWithBank(inv._id)}
                            >
                              Verify With Bank
                            </button>
                          ) : (
                            <span className="done-text">Done</span>
                          )}
                        </td>

                        <td>
                          {inv.accountVerified ? (
                            <div className="interest-form-box">
                              <select
                                className="field"
                                value={selectedQuarter}
                                onChange={(e) =>
                                  handleInterestChange(
                                    inv._id,
                                    "quarterLabel",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select Quarter</option>
                                {quarterOptions.map((q) => (
                                  <option
                                    key={q.value}
                                    value={q.value}
                                    disabled={q.disabled}
                                  >
                                    {q.label}
                                    {q.disabled ? " (Added)" : ""}
                                  </option>
                                ))}
                              </select>

                              <input
                                className="field"
                                type="text"
                                value={`₹ ${autoInterest.toFixed(2)}`}
                                readOnly
                              />

                              <input
                                className="field"
                                type="date"
                                value={
                                  interestForm[inv._id]?.paidDate ||
                                  new Date().toISOString().split("T")[0]
                                }
                                onChange={(e) =>
                                  handleInterestChange(
                                    inv._id,
                                    "paidDate",
                                    e.target.value,
                                  )
                                }
                              />

                              <button
                                className="primary-btn full-btn"
                                onClick={() => addQuarterlyInterest(inv)}
                              >
                                Send To Admin
                              </button>
                            </div>
                          ) : (
                            <span className="muted-text">
                              Verify deposit first
                            </span>
                          )}
                        </td>

                        <td>
                          {inv.quarterlyInterests?.length > 0 ? (
                            <div className="interest-entry-list">
                              {inv.quarterlyInterests
                                .slice()
                                .sort(
                                  (a, b) =>
                                    new Date(b.paidDate) - new Date(a.paidDate),
                                )
                                .map((q) => (
                                  <div
                                    key={q._id}
                                    className="interest-entry-card"
                                  >
                                    <div className="interest-row">
                                      <span className="entry-label">
                                        Quarter
                                      </span>
                                      <span>{q.quarterLabel}</span>
                                    </div>

                                    <div className="interest-row">
                                      <span className="entry-label">
                                        Amount
                                      </span>
                                      <span>₹ {q.amount}</span>
                                    </div>

                                    <div className="interest-row">
                                      <span className="entry-label">Date</span>
                                      <span>
                                        {q.paidDate
                                          ? new Date(
                                              q.paidDate,
                                            ).toLocaleDateString("en-IN")
                                          : "-"}
                                      </span>
                                    </div>

                                    <div className="interest-row">
                                      <span className="entry-label">
                                        Status
                                      </span>

                                      {!q.adminApproved ? (
                                        <span className="badge badge-warning">
                                          Pending
                                        </span>
                                      ) : q.sentToCustomer ? (
                                        <span className="badge badge-success">
                                          Sent to Client
                                        </span>
                                      ) : (
                                        <button
                                          className="primary-btn"
                                          style={{
                                            padding: "4px 10px",
                                            fontSize: "12px",
                                          }}
                                          onClick={() =>
                                            sendInterestToCustomer(
                                              inv._id,
                                              q._id,
                                            )
                                          }
                                        >
                                          Send To Client
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <span className="muted-text">No entries</span>
                          )}
                        </td>

                        <td>
                          <button
                            className="primary-btn"
                            style={{ background: "#2563eb" }}
                            onClick={() => downloadQuarterSchedulePDF(inv)}
                          >
                            Download Form
                          </button>
                        </td>

                        <td>
                          <button
                            className="primary-btn"
                            style={{ background: "#16a34a" }}
                            onClick={() => releaseCapital(inv._id)}
                          >
                            Release Capital
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="13" className="empty-cell">
                      No active investments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div style={{ marginTop: "20px", marginBottom: "10px" }}>
          <button
            className="primary-btn"
            onClick={() => setShowArchived((prev) => !prev)}
            style={{ background: "#374151" }}
          >
            {showArchived
              ? "Hide Archived Investments"
              : "View Archived Investments"}
          </button>
        </div>

        {showArchived && (
          <section className="panel">
            <div className="panel-header">
              <h2>Archived Investments</h2>
            </div>

            <div className="table-wrap">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Investor ID</th>
                    <th>Investor Name</th>
                    <th>Deposit Amount</th>
                    <th>Interest Rate</th>
                    <th>Tenure</th>
                    <th>Deposit Date</th>
                    <th>Maturity Date</th>
                    <th>Released</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {archivedInvestments.length > 0 ? (
                    archivedInvestments.map((inv) => (
                      <tr key={inv._id}>
                        <td>{inv.investorId || "-"}</td>
                        <td>{inv.customerName || "-"}</td>
                        <td>₹ {inv.depositAmount || 0}</td>
                        <td>{inv.interestRate || 9}%</td>
                        <td>
                          {inv.tenure ? `${inv.tenure / 12} Years` : "5 Years"}
                        </td>
                        <td>
                          {inv.depositDate
                            ? new Date(inv.depositDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "-"}
                        </td>
                        <td>
                          {inv.maturityDate
                            ? new Date(inv.maturityDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "-"}
                        </td>
                        <td>
                          <span className="badge badge-success">
                            {inv.isReleased ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-success">
                            Deposit Released
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="empty-cell">
                        No archived investments found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
